// Caret-binding relation promotion (Workstream C ranking signal S6).
//
// When the caret sits inside an alias binding's definition span at a relation
// slot — re-editing `INNER JOIN ▮customer c ON c.customer_id = …` or
// `FROM ▮rental r WHERE r.return_date IS NULL` — the relation already written
// there is the best suggestion by construction: the rest of the statement
// (the ON pairing, the WHERE references through the alias) was authored
// against it. S1/S4 promote it into tier 1, but a dozen FK-linked /
// buffer-referenced siblings share that tier and alphabetical order buries
// it. This promotes exactly the caret binding's relation and demotes the
// other tier-1 table/view candidates to tier 2.
//
// Deliberately narrow: fires only when the caret is inside a table/view
// binding's `definedAt` span AND a table/view candidate matches that
// relation (at any tier — mssql #temp / @table-var synthetics are emitted at
// tier 2 and get lifted, mssql 45.§5). CTE / snippet / keyword candidates are
// never touched, so CTE-first (S5) expectations keep their order.

import type { AliasBinding, CompletionCandidate } from '../../../core/engines';

const stripQuotes = (s: string): string => s.replace(/[[\]"`]/g, '');

const labelText = (c: CompletionCandidate): string =>
  typeof c.label === 'string' ? c.label : ((c.label as { label?: string })?.label ?? '');

/**
 * Promote the relation of the alias binding whose definition span contains
 * `offset`: the matching candidate moves to tier 1 (from whatever tier it
 * was emitted at — mssql #temp / @table-variable synthetics arrive at
 * tier 2, mssql script.45 §5) while every other tier-1 table/view candidate
 * drops to tier 2. Mutates `out` in place; no-op when the caret isn't inside
 * a table/view binding or no candidate matches the binding's relation (bare
 * or schema-qualified label).
 */
export function promoteCaretBindingRelation(
  out: CompletionCandidate[],
  bindings: readonly AliasBinding[],
  offset: number,
): void {
  const binding = bindings.find(
    (b) =>
      (b.kind === 'table' || b.kind === 'view') &&
      b.definedAt.start <= offset &&
      offset <= b.definedAt.end,
  );
  if (!binding) return;
  const rel = stripQuotes(binding.relation).toLowerCase();
  const matches = (c: CompletionCandidate): boolean => {
    const l = stripQuotes(labelText(c)).toLowerCase();
    return l === rel || l.endsWith(`.${rel}`);
  };
  const isTableLike = (c: CompletionCandidate): boolean =>
    c.kind === 'table' || c.kind === 'view';
  if (!out.some((c) => isTableLike(c) && matches(c))) return;
  for (let i = 0; i < out.length; i++) {
    const c = out[i]!;
    if (!isTableLike(c)) continue;
    if (matches(c)) {
      if (c.tier !== 1) out[i] = { ...c, tier: 1 };
    } else if (c.tier === 1) {
      out[i] = { ...c, tier: 2 };
    }
  }
}
